import { useContext } from 'react'

import type { UseFormOptions } from '@domain-group/react-hooks'
import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import defaultState, { requiredFields } from '@models/publicationPaginationRule'
import type { PublicationPaginationRuleModel, PublicationPaginationRuleRequestOptions } from '@models/types'

import * as publicationPaginationRuleActions from '@redux/modules/publicationPaginationRule'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/module'

type CreatePublicationPaginationRuleParams = {
  dispatch: AppDispatch,
  publicationPaginationRuleParams: Partial<PublicationPaginationRuleModel>,
  requestOptions?: PublicationPaginationRuleRequestOptions,
}

const createPublicationPaginationRule = (params: CreatePublicationPaginationRuleParams) => {
  const { publicationPaginationRuleParams, dispatch, requestOptions } = params
  const { createPublicationPaginationRule: createFn } = publicationPaginationRuleActions

  return dispatch(createFn(publicationPaginationRuleParams, requestOptions))
}

export type ManagePublicationPaginationRuleParams = {
  action?: 'assign' | 'unassign',
  provider_id?: number,
  pagination_rule_id: number,
  publication_ids?: number[],
  string?: string,
}

export type manageForPaginationRuleParams = {
  manageParams: ManagePublicationPaginationRuleParams,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const manageForPaginationRule = (params: manageForPaginationRuleParams) => {
  const { manageParams, dispatch, requestOptions } = params
  const { manageForPaginationRule: manageFn } = publicationPaginationRuleActions

  return dispatch(manageFn(manageParams, requestOptions))
}

export type DeletePublicationPaginationRuleParams = {
  publicationPaginationRule: PublicationPaginationRuleModel,
  dispatch: AppDispatch,
}

const deletePublicationPaginationRule = (params: DeletePublicationPaginationRuleParams) => {
  const { publicationPaginationRule, dispatch } = params
  const { deletePublicationPaginationRule: deleteFn } = publicationPaginationRuleActions

  return dispatch(deleteFn(publicationPaginationRule))
}

type CustomPayload = {
  action?: 'assign' | 'unassign',
}

export type ShowManagePublicationPaginationRulePayload = {
  customPayload?: CustomPayload,
  publicationPaginationRule: Partial<PublicationPaginationRuleModel>,
}

type ManagePublicationPaginationRulePayload = {
  publicationPaginationRule: Partial<PublicationPaginationRuleModel>,
  showManagePublicationPaginationRulesModal: (payload: ShowManagePublicationPaginationRulePayload) => void,
  customPayload?: CustomPayload,
}

const managePublicationPaginationRules = (
  params: ManagePublicationPaginationRulePayload,
) => new Promise((resolve, reject) => {
  const { publicationPaginationRule, customPayload = {}, showManagePublicationPaginationRulesModal } = params

  if (showManagePublicationPaginationRulesModal){
    const payload = {
      publicationPaginationRule,
      ...customPayload,
    }

    showManagePublicationPaginationRulesModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showManagePublicationPaginationRulesModal not defined in PageContext callbacks'))
})

type CreatePublicationPaginationRulePayload = {
  publicationPaginationRule: PublicationPaginationRuleModel,
  showCreatePublicationPaginationRuleModal: Function,
}

const addPublicationPaginationRule = (params: CreatePublicationPaginationRulePayload) => (
  new Promise((resolve, reject) => {
    const { publicationPaginationRule, showCreatePublicationPaginationRuleModal } = params

    if (showCreatePublicationPaginationRuleModal){
      const payload = {
        publicationPaginationRule,
      }

      showCreatePublicationPaginationRuleModal(payload)

      resolve({ success: true, result: payload })
    }

    reject(new Error('showCreatePublicationPaginationRuleModal not defined in PageContext callbacks'))
  })
)

export function usePublicationPaginationRuleForm(
  publicationPaginationRule: Partial<PublicationPaginationRuleModel> = {},
  options: UseFormOptions = {},
) {
  const { validateOn } = options

  const publicationPaginationRuleForm = useForm(
    defaultState,
    { entity: publicationPaginationRule, requiredFields, validateOn },
    [publicationPaginationRule.id, publicationPaginationRule.cache_key],
  )

  return {
    ...publicationPaginationRuleForm,
  }
}

export function useRelations(publicationPaginationRule: PublicationPaginationRuleModel) {
  const { pagination_rule_id, publication_id } = publicationPaginationRule || {}

  const { paginationRules, publications } = useSelector(reduxState => reduxState.entities)

  const paginationRule = paginationRules[pagination_rule_id] || {}
  const publication = publications[publication_id] || {}

  return {
    paginationRule,
    publication,
  }
}

function usePublicationPaginationRule(initEntity: Partial<PublicationPaginationRuleModel> = {}) {
  // @ts-expect-error TS2345
  const { entity: publicationPaginationRule } = useLatestEntity(initEntity, 'publicationPaginationRules')

  const dispatch = useThunkDispatch()

  const { callbacks } = useContext(PageContext)
  const {
    showCreatePublicationPaginationRuleModal,
    showManagePublicationPaginationRulesModal,
  } = callbacks || {}

  const { creating, deleting, updating } = useSelector(reduxState => reduxState.publicationPaginationRules)

  // @ts-expect-error TS2345
  const { paginationRule } = useRelations(publicationPaginationRule)

  return {
    callbacks: {
      addPublicationPaginationRule: () => addPublicationPaginationRule({
        // @ts-expect-error TS2322
        publicationPaginationRule, showCreatePublicationPaginationRuleModal,
      }),
      createPublicationPaginationRule: (
        publicationPaginationRuleParams: Partial<PublicationPaginationRuleModel>,
        requestOptions?: PublicationPaginationRuleRequestOptions,
      ) => createPublicationPaginationRule({ publicationPaginationRuleParams, dispatch, requestOptions }),
      // @ts-expect-error TS2322
      deletePublicationPaginationRule: () => deletePublicationPaginationRule({ publicationPaginationRule, dispatch }),
      manageForPaginationRule: (manageParams: ManagePublicationPaginationRuleParams, requestOptions: EntityOptions) => (
        manageForPaginationRule({ manageParams, dispatch, requestOptions })
      ),
      managePublicationPaginationRules: (customPayload: CustomPayload) => managePublicationPaginationRules({
        // @ts-expect-error TS2322
        publicationPaginationRule, customPayload, showManagePublicationPaginationRulesModal,
      }),
    },
    creating,
    deleting,
    paginationRule,
    publicationPaginationRule,
    updating,
  }
}

export default usePublicationPaginationRule
